import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { cn } from '@/lib/utils';
import { TableCell, TableRow } from '@/components/ui/table';

import { NULL_VALUE } from 'constants/displayConstants';
import AssignmentCardView, {
  AssignmentRowHeaderActions,
} from './AssignmentCardView';
import AssignmentCardEdit from './AssignmentCardEdit';
import Ics204HeaderWorkflowMenu from './Ics204HeaderWorkflowMenu';
import { useSectionChiefResolver } from './useSectionChiefResolver';
import {
  WorkGroupResourceNeed,
  SimplifiedWorkAssignment,
} from './AssignmentList';
import type { ProposedStatusOption } from './assignmentStatus';
import {
  PlannedResource,
  type WalaSignature,
  type WalaVersion,
} from 'components/IAP/hooks/assignmentListHooks';
import {
  formatIcs204StatusCell,
  isVersionLockedBySignatures,
  parseWalaSignatures,
} from './ics204SignatureSlots';
import type { Ics204EditingSection } from './ics204Sections';

export type AssignmentListRow = {
  id?: string;
  walaId: string | null;
  workGroupId: string;
  workGroup: string;
  branch: string;
  division: string;
  walaGroup: string;
  stagingArea: string;
  proposedStatus: ProposedStatusOption;
  actualStatus: string | null;
  sectionChief?: string;
  sectionChiefContact?: string;
  branchDirector?: string;
  branchDirectorContact?: string;
  divisionGroupSupervisor?: string;
  divisionGroupSupervisorContact?: string;
  workAssignments: SimplifiedWorkAssignment[];
  workGroupResourceNeeds: WorkGroupResourceNeed[];
  plannedResources: PlannedResource[] | null;
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  specialInstructions?: string;
  signatures?: WalaSignature[];
  updatedAt?: string;
  updatedByUserName?: string | null;
};

interface AssignmentCardProps {
  columns: string[];
  item: AssignmentListRow;
  periodNumber: number;
  readOnly?: boolean;
  /** Work groups already claimed by a card in this period, this one included. */
  alreadyAddedWorkGroupIds?: string[];
  /** Total table columns for the expanded colSpan. */
  columnCount: number;
  /** Narrow layout when PRATUS AI sidebar is open — wrap summary cells. */
  compact?: boolean;
}

const cellValue = (item: AssignmentListRow, column: string) => {
  const value = item[column as keyof AssignmentListRow];
  if (value === null || value === undefined) {
    return NULL_VALUE;
  }
  return String(value) || NULL_VALUE;
};

/**
 * One ICS-204 as a table tbody: summary row + optional expanded detail/edit
 * row. Using tbody-per-item keeps Playwright's ics204-card → content
 * descendants valid.
 */
const AssignmentContainer: React.FC<AssignmentCardProps> = ({
  columns,
  item,
  periodNumber,
  readOnly = false,
  alreadyAddedWorkGroupIds = [],
  columnCount,
  compact = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const openWala = searchParams.get('openWala');
  const openFromNotification =
    !!openWala && !!item.walaId && openWala === item.walaId;

  const [expanded, setExpanded] = useState(openFromNotification);
  const [editingSection, setEditingSection] =
    useState<Ics204EditingSection | null>(null);
  const [isCreatingSignedVersion, setIsCreatingSignedVersion] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<WalaVersion | null>(
    null
  );
  const headerRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (!openFromNotification) return;
    setExpanded(true);
    headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openWala');
        return next;
      },
      { replace: true }
    );
  }, [openFromNotification, setSearchParams]);

  const { isCurrentUserSectionChief: isCurrentUserReviewer, sectionChief } =
    useSectionChiefResolver(item.workGroupId);

  const tipSignatures = parseWalaSignatures(item.signatures);
  const signatureLocked = isVersionLockedBySignatures(tipSignatures);
  const contentLocked =
    !!viewingVersion || (signatureLocked && !isCreatingSignedVersion);

  const displayItem = useMemo((): AssignmentListRow => {
    if (!viewingVersion) return item;
    return {
      ...item,
      branch: viewingVersion.branch ?? '',
      division: viewingVersion.division ?? '',
      walaGroup: viewingVersion.walaGroup ?? '',
      stagingArea: viewingVersion.stagingArea ?? '',
      sectionChief: viewingVersion.sectionChief ?? undefined,
      sectionChiefContact: viewingVersion.sectionChiefContact ?? undefined,
      branchDirector: viewingVersion.branchDirector ?? undefined,
      branchDirectorContact: viewingVersion.branchDirectorContact ?? undefined,
      divisionGroupSupervisor:
        viewingVersion.divisionGroupSupervisor ?? undefined,
      divisionGroupSupervisorContact:
        viewingVersion.divisionGroupSupervisorContact ?? undefined,
      medicalCommunication: viewingVersion.medicalCommunication ?? undefined,
      evacuationCommunication:
        viewingVersion.evacuationCommunication ?? undefined,
      otherCommunication: viewingVersion.otherCommunication ?? undefined,
      specialInstructions: viewingVersion.specialInstructions ?? undefined,
      plannedResources:
        (viewingVersion.resourceSnapshot as PlannedResource[] | null) ??
        item.plannedResources,
      signatures: parseWalaSignatures(viewingVersion.signatures),
    };
  }, [item, viewingVersion]);

  const startCreateSignedVersion = () => {
    setViewingVersion(null);
    setEditingSection(null);
    setIsCreatingSignedVersion(true);
    setExpanded(true);
  };

  const cancelCreateSignedVersion = () => {
    setIsCreatingSignedVersion(false);
  };

  const toggleExpanded = () => {
    if (editingSection || isCreatingSignedVersion) return;
    setExpanded((prev) => !prev);
  };

  // Create-signed still uses the full-card edit form for Sign payload collection.
  if (isCreatingSignedVersion && !readOnly) {
    return (
      <tbody data-testid="ics204-card">
        <TableRow className="tw:hover:bg-transparent">
          <TableCell
            colSpan={columnCount}
            className="tw:h-auto tw:whitespace-normal tw:p-0 tw:align-top"
          >
            <AssignmentCardEdit
              sectionChief={sectionChief}
              row={item}
              setIsEditing={(next) => {
                if (!next) setIsCreatingSignedVersion(false);
              }}
              columnNumber={columns.length}
              periodNumber={periodNumber}
              alreadyAddedWorkGroupIds={alreadyAddedWorkGroupIds}
              isCreatingSignedVersion={isCreatingSignedVersion}
              onStartCreateSignedVersion={startCreateSignedVersion}
              onCancelCreateSignedVersion={cancelCreateSignedVersion}
              onSignedVersionCreated={() => {
                setIsCreatingSignedVersion(false);
              }}
              onUnsignedVersionCreated={() => {
                setIsCreatingSignedVersion(false);
              }}
              viewingVersionId={null}
              onViewVersion={(version) => {
                setIsCreatingSignedVersion(false);
                setViewingVersion(version as WalaVersion);
                setExpanded(true);
              }}
              onViewLatest={() => setViewingVersion(null)}
            />
          </TableCell>
        </TableRow>
      </tbody>
    );
  }

  return (
    <tbody data-testid="ics204-card">
      <TableRow
        ref={headerRef}
        data-testid="ics204-card-header"
        className="tw:cursor-pointer tw:bg-black tw:hover:bg-black"
        aria-expanded={expanded}
        onClick={toggleExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
          }
        }}
        tabIndex={0}
      >
        <TableCell
          className={cn(
            'tw:w-8 tw:bg-black',
            compact && 'tw:whitespace-normal'
          )}
        >
          <ChevronDown
            className={cn(
              'tw:h-4 tw:w-4 tw:transition-transform',
              expanded && 'tw:rotate-180'
            )}
          />
        </TableCell>
        {columns.map((column) => (
          <TableCell
            key={column}
            className={cn(
              'tw:min-w-0 tw:bg-black tw:text-left',
              compact && 'tw:whitespace-normal'
            )}
          >
            {column === 'proposedStatus' ? (
              (() => {
                const statusCell = formatIcs204StatusCell({
                  proposedStatusLabel: item.proposedStatus,
                  signatures: tipSignatures,
                });
                return (
                  <div
                    className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5"
                    data-testid="ics204-status-cell"
                  >
                    <span className="tw:truncate">{statusCell.primary}</span>
                    <span
                      className="tw:truncate tw:text-xs tw:text-[var(--text-weak)]"
                      data-testid="ics204-status-signatures"
                    >
                      {statusCell.signaturesLine}
                    </span>
                  </div>
                );
              })()
            ) : (
              <span className={cn(compact && 'tw:break-words')}>
                {cellValue(item, column)}
              </span>
            )}
          </TableCell>
        ))}
        <TableCell
          className={cn(
            'tw:bg-black tw:text-right',
            compact && 'tw:whitespace-normal'
          )}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="tw:flex tw:items-center tw:justify-end tw:gap-1">
            <Ics204HeaderWorkflowMenu
              walaId={item.walaId}
              actualStatus={item.actualStatus}
              isCurrentUserReviewer={isCurrentUserReviewer}
              disabled={!!viewingVersion}
            />
            <AssignmentRowHeaderActions
              item={item}
              isCurrentUserReviewer={isCurrentUserReviewer}
              periodNumber={periodNumber}
              readOnly={readOnly || !!viewingVersion}
            />
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="tw:hover:bg-transparent">
          <TableCell
            colSpan={columnCount}
            className="tw:h-auto tw:min-w-0 tw:whitespace-normal tw:p-0 tw:align-top"
          >
            <AssignmentCardView
              sectionChief={sectionChief}
              item={displayItem}
              tipItem={item}
              periodNumber={periodNumber}
              readOnly={readOnly}
              contentLocked={contentLocked}
              editingSection={editingSection}
              onEditSection={(section) => {
                setViewingVersion(null);
                setEditingSection(section);
              }}
              onCancelSectionEdit={() => setEditingSection(null)}
              isCreatingSignedVersion={isCreatingSignedVersion}
              viewingVersionId={viewingVersion?.id ?? null}
              onStartCreateSignedVersion={startCreateSignedVersion}
              onCancelCreateSignedVersion={cancelCreateSignedVersion}
              onSignedVersionCreated={() => {
                setIsCreatingSignedVersion(false);
              }}
              onUnsignedVersionCreated={() => {
                setIsCreatingSignedVersion(false);
                setViewingVersion(null);
              }}
              onViewVersion={(version) => {
                setEditingSection(null);
                setViewingVersion(version as WalaVersion);
              }}
              onViewLatest={() => setViewingVersion(null)}
            />
          </TableCell>
        </TableRow>
      )}
    </tbody>
  );
};

export default AssignmentContainer;
